import {
  CategoryOutlined,
  Inventory2Outlined,
  LocalOfferOutlined,
} from "@mui/icons-material";
import {
  Box,
  Chip,
  Stack,
  SxProps,
  Theme,
  Typography,
  alpha,
  styled,
} from "@mui/material";
import Image from "mui-image";
import GlassCard from "Components/GlassCard/GlassCard";
import { ApiDimension, ApiProduct } from "@incendium/api";
import { RouteNames } from "Hooks/routeNames";
import useProtectedRoutes from "Hooks/useProtectedRoutes";
import { useSelectedProject } from "Hooks";
import { Link } from "react-router-dom";
import { ReactNode } from "react";

const HeroCard = styled(GlassCard)(({ theme }) => ({
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 3,
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${alpha(theme.palette.primary.main, 0.25)})`,
  },
}));

const ImageWrap = styled(Box)(({ theme }) => ({
  width: 88,
  height: 88,
  borderRadius: theme.shape.borderRadius,
  overflow: "hidden",
  flexShrink: 0,
  border: `1px solid ${theme.palette.grey[200]}`,
  backgroundColor: theme.palette.grey[50],
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const MetaIcon = styled(Box)(({ theme }) => ({
  width: 88,
  height: 88,
  borderRadius: theme.shape.borderRadius,
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: theme.palette.primary.main,
  backgroundColor: alpha(theme.palette.primary.main, 0.08),
}));

interface ProductDeepDiveHeroProps {
  dimension: ApiDimension;
  selectedProduct?: ApiProduct;
  selectedProductCategory?: string;
  selectedProductBrand?: string;
  sx?: SxProps<Theme>;
}

function ProductDeepDiveHero({
  dimension,
  selectedProduct,
  selectedProductCategory,
  selectedProductBrand,
  sx,
}: ProductDeepDiveHeroProps) {
  const { selectedProject, selectedClient } = useSelectedProject();
  const { pathByName } = useProtectedRoutes();

  const title =
    selectedProduct?.name ||
    selectedProductCategory ||
    selectedProductBrand ||
    "";

  const pageLabel =
    dimension === ApiDimension.DIMENSION_PRODUCT_CATEGORY
      ? "Category deep dive"
      : dimension === ApiDimension.DIMENSION_PRODUCT_BRAND
        ? "Brand deep dive"
        : "Product deep dive";

  let icon: ReactNode = <Inventory2Outlined sx={{ fontSize: 40 }} />;
  if (dimension === ApiDimension.DIMENSION_PRODUCT_CATEGORY) {
    icon = <CategoryOutlined sx={{ fontSize: 40 }} />;
  } else if (dimension === ApiDimension.DIMENSION_PRODUCT_BRAND) {
    icon = <LocalOfferOutlined sx={{ fontSize: 40 }} />;
  }

  const categoryPath =
    selectedClient?.id && selectedProject?.id
      ? pathByName(RouteNames.PRODUCT_CATEGORY_DEEP_DIVE, {
          clientId: selectedClient.id,
          projectId: selectedProject.id,
        })
      : "#";

  const brandPath =
    selectedClient?.id && selectedProject?.id
      ? pathByName(RouteNames.PRODUCT_BRAND_DEEP_DIVE, {
          clientId: selectedClient.id,
          projectId: selectedProject.id,
        })
      : "#";

  const categoryName =
    selectedProduct?.category || selectedProduct?.productCategory?.name;

  return (
    <HeroCard
      sx={{ width: "100%", ...sx }}
      boxProps={{ px: 2.5, py: 2, width: "100%", height: "100%" }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ xs: "flex-start", sm: "center" }}
        sx={{ height: "100%" }}
      >
        {dimension === ApiDimension.DIMENSION_PRODUCT_NAME &&
        selectedProduct?.imageUrl ? (
          <ImageWrap>
            <Image
              src={selectedProduct.imageUrl}
              alt={title}
              height={88}
              width={88}
              fit="contain"
              duration={0}
            />
          </ImageWrap>
        ) : (
          <MetaIcon>{icon}</MetaIcon>
        )}

        <Box flex={1} minWidth={0}>
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ lineHeight: 1.2, letterSpacing: 0.6 }}
          >
            {pageLabel}
          </Typography>
          <Typography variant="h5" fontWeight={700} noWrap title={title}>
            {title}
          </Typography>
          {dimension === ApiDimension.DIMENSION_PRODUCT_NAME && (
            <Stack
              direction="row"
              flexWrap="wrap"
              gap={1}
              mt={1.25}
              alignItems="center"
            >
              {categoryName ? (
                <Link
                  to={{
                    pathname: categoryPath,
                    state: {
                      selectedProductCategory: categoryName,
                    },
                  }}
                  style={{ textDecoration: "none" }}
                >
                  <Chip
                    size="small"
                    clickable
                    label={categoryName}
                    color="primary"
                    variant="outlined"
                  />
                </Link>
              ) : null}
              {selectedProduct?.brand ? (
                <Link
                  to={{
                    pathname: brandPath,
                    state: { selectedProductBrand: selectedProduct.brand },
                  }}
                  style={{ textDecoration: "none" }}
                >
                  <Chip
                    size="small"
                    clickable
                    label={selectedProduct.brand}
                    color="primary"
                    variant="outlined"
                  />
                </Link>
              ) : null}
            </Stack>
          )}
        </Box>
      </Stack>
    </HeroCard>
  );
}

export default ProductDeepDiveHero;
